import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { biometricEnrolled, unlockBiometric, enrollBiometric, platformAuthenticatorAvailable, biometricSetUpOnThisDevice, biometricOfferDismissed, dismissBiometricOffer } from './webauthn.js';

type Status = { initialised: boolean; unlocked: boolean };
const wrap: React.CSSProperties = { minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)' };
const card: React.CSSProperties = { width: 380, maxWidth: '92vw', padding: 28, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: 'var(--shadow)' };
const input: React.CSSProperties = { width: '100%', padding: '10px 12px', border: '1px solid var(--border2)', borderRadius: 8, fontSize: 14, margin: '6px 0', fontFamily: 'var(--sans)' };
const btn: React.CSSProperties = { width: '100%', padding: 11, border: 'none', borderRadius: 8, background: 'var(--accent)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 6 };

function Brand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 18 }}>
      <div className="brand-mark">S</div>
      <div><div style={{ fontWeight: 600 }}>SOR</div><div style={{ fontSize: 11, color: 'var(--ink3)' }}>Compliance Records</div></div>
    </div>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [st, setSt] = useState<Status | null>(null);
  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState<string | null>(null);
  const [bioAvail, setBioAvail] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const [offerBio, setOfferBio] = useState(false);
  const [installEvt, setInstallEvt] = useState<{ prompt: () => void } | null>(null);
  const [standalone] = useState(() => typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true));

  const refresh = () => fetch('/api/auth/status', { cache: 'no-store' }).then((r) => r.json()).then(setSt).catch(() => setSt({ initialised: false, unlocked: false }));
  useEffect(() => { refresh(); }, []);
  useEffect(() => { biometricEnrolled().then(setBioAvail).catch(() => setBioAvail(false)); }, []);
  // Capture the browser's PWA install prompt so we can offer a real "Install app" button.
  useEffect(() => {
    const h = (e: Event) => { e.preventDefault(); setInstallEvt(e as unknown as { prompt: () => void }); };
    window.addEventListener('beforeinstallprompt', h);
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);

  async function biometric() {
    setErr(null); setBioBusy(true);
    try { await unlockBiometric(); await refresh(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBioBusy(false); }
  }

  if (!st) return <div style={wrap}><div className="muted">Loading…</div></div>;
  if (st.unlocked) return <>{children}</>;

  async function setup(e: FormEvent) {
    e.preventDefault(); setErr(null);
    if (pass.length < 10) return setErr('Use at least 10 characters (a passphrase of several words is best).');
    if (pass !== pass2) return setErr('Passphrases do not match.');
    setBusy(true);
    try {
      const r = await fetch('/api/auth/setup', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ passphrase: pass }) });
      const j = await r.json();
      if (!r.ok) { setErr(j.error ?? 'Failed'); return; }
      setRecovery(j.recoveryCode);
    } catch { setErr('Could not reach the server'); } finally { setBusy(false); }
  }
  async function login(e: FormEvent) {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      const r = await fetch('/api/auth/login', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ passphrase: pass }) });
      if (!r.ok) { setErr((await r.json()).error ?? 'Failed'); return; }
      // Offer to set up biometric unlock on this device (first time, if supported).
      const offer = !biometricSetUpOnThisDevice() && !biometricOfferDismissed() && await platformAuthenticatorAvailable();
      if (offer) { setOfferBio(true); return; }
      await refresh();
    } catch { setErr('Could not reach the server'); } finally { setBusy(false); }
  }
  async function enableBioNow() {
    setErr(null); setBioBusy(true);
    try { await enrollBiometric('This device'); await refresh(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBioBusy(false); }
  }
  function skipBio() { dismissBiometricOffer(); setOfferBio(false); refresh(); }

  if (offerBio) {
    return (
      <div style={wrap}><div style={card}>
        <Brand />
        <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>Enable biometric unlock?</h2>
        <p className="muted" style={{ fontSize: 13 }}>
          Unlock next time with your fingerprint or face instead of typing your passphrase. Your passphrase
          still works and stays the way to recover your vault. Setup uses your device’s biometric prompt.
        </p>
        {err && <div style={{ color: 'var(--bad)', fontSize: 12.5, margin: '6px 0' }}>{err}</div>}
        <button style={{ ...btn, opacity: bioBusy ? 0.7 : 1 }} type="button" disabled={bioBusy} onClick={enableBioNow}>
          {bioBusy ? 'Follow the prompt…' : '🔓 Enable biometric unlock'}
        </button>
        <button style={{ ...btn, background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--border2)' }} type="button" disabled={bioBusy} onClick={skipBio}>
          Not now
        </button>
      </div></div>
    );
  }

  if (recovery) {
    return (
      <div style={wrap}><div style={card}>
        <Brand />
        <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>Save your recovery code</h2>
        <p className="muted" style={{ fontSize: 13 }}>This is shown <strong>once</strong>. If you forget your passphrase, this is the <strong>only</strong> way to recover your encrypted records. Write it down and store it somewhere safe and offline.</p>
        <div className="mono" style={{ fontSize: 15, background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 8, padding: 12, textAlign: 'center', letterSpacing: 1, margin: '12px 0', wordBreak: 'break-all' }}>{recovery}</div>
        <button style={btn} onClick={async () => { await refresh(); setRecovery(null); }}>I have saved it — continue</button>
      </div></div>
    );
  }

  return (
    <div style={wrap}><div style={card}>
      <Brand />
      {!standalone && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'var(--accentSoft)', border: '1px solid var(--border2)', borderRadius: 11, padding: '12px 14px', marginBottom: 18 }}>
          <img src="/icon-192.png" alt="SOR" width={42} height={42} style={{ borderRadius: 10, flexShrink: 0, boxShadow: '0 1px 3px rgba(16,24,40,0.18)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Install the SOR app</div>
            <div className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>One-tap access on your phone, with the camera for device photos.</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {installEvt && (
                <button type="button" style={{ ...btn, width: 'auto', margin: 0, padding: '8px 16px', fontSize: 13 }}
                  onClick={() => { installEvt.prompt(); setInstallEvt(null); }}>
                  Install app
                </button>
              )}
              <a href="/download/sor.apk" download
                style={{ ...btn, width: 'auto', margin: 0, padding: '8px 16px', fontSize: 13, textDecoration: 'none', display: 'inline-block', ...(installEvt ? { background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--border2)' } : {}) }}>
                {installEvt ? 'Android .apk' : 'Install Android app (.apk)'}
              </a>
            </div>
          </div>
        </div>
      )}
      <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>{st.initialised ? 'Unlock your vault' : 'Set a master passphrase'}</h2>
      <p className="muted" style={{ fontSize: 13 }}>
        {st.initialised
          ? 'Enter your master passphrase to decrypt and access your records.'
          : 'This passphrase encrypts everything in your vault. There is no backdoor — choose a strong passphrase (several random words) and keep it safe.'}
      </p>
      <form onSubmit={st.initialised ? login : setup}>
        <input style={input} type="password" placeholder="Master passphrase" value={pass} onChange={(e) => setPass(e.target.value)} autoFocus />
        {!st.initialised && <input style={input} type="password" placeholder="Confirm passphrase" value={pass2} onChange={(e) => setPass2(e.target.value)} />}
        {err && <div style={{ color: 'var(--bad)', fontSize: 12.5, margin: '4px 0' }}>{err}</div>}
        <button style={{ ...btn, opacity: busy ? 0.7 : 1 }} type="submit" disabled={busy}>
          {busy ? 'Working…' : st.initialised ? 'Unlock' : 'Create vault'}
        </button>
      </form>
      {st.initialised && bioAvail && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 10px', color: 'var(--ink3)', fontSize: 12 }}>
            <span style={{ flex: 1, height: 1, background: 'var(--border)' }} /> or <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>
          <button style={{ ...btn, background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--border2)', opacity: bioBusy ? 0.7 : 1 }}
            type="button" disabled={bioBusy} onClick={biometric}>
            {bioBusy ? 'Waiting for biometric…' : '🔓 Unlock with fingerprint / face'}
          </button>
        </>
      )}
    </div></div>
  );
}
